import React from 'react';
import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getFirstAllowedReportPath } from './reportAccessUtils';

const RequireCommissionAccess: React.FC = () => {
  const outletContext = useOutletContext<{ userData?: { allowedReports?: string[] } }>();
  const { isBrokerFamily, hideCommissions, claims } = useAuth();
  const location = useLocation();

  // Marketing role: default access is the Lead Inbox. When hideCommissions is true
  // OR allowedReports is empty (the default for new marketing users), redirect away
  // from commission pages. If the admin has granted reports, fall through to the
  // first allowed report. If neither, send to /leads/inbox.
  if (claims?.role === 'marketing') {
    const allowed = outletContext?.userData?.allowedReports;
    const hasAllowedReports = Array.isArray(allowed) && allowed.length > 0;
    if (hideCommissions || !hasAllowedReports) {
      const target = hasAllowedReports
        ? getFirstAllowedReportPath(allowed)
        : '/leads/inbox';
      return <Navigate to={target} replace />;
    }
  }

  if (isBrokerFamily && hideCommissions) {
    const target = getFirstAllowedReportPath(outletContext?.userData?.allowedReports);
    return <Navigate to={target} replace />;
  }

  // Redirect users without company-wide access from the main dashboard
  // to their first assigned office dashboard. Only super_admin, founder,
  // and users with canSeeAllOffices can view the company-wide dashboard.
  if (location.pathname === '/') {
    const isCompanyWide =
      claims?.role === 'super_admin' ||
      claims?.isFounder ||
      claims?.canSeeAllOffices;

    if (!isCompanyWide) {
      const officeId = claims?.officeIds?.[0];
      if (officeId) {
        return <Navigate to={`/offices/${officeId}`} replace />;
      }
    }
  }

  return <Outlet context={outletContext} />;
};

export default RequireCommissionAccess;
